import { observer } from 'mobx-react-lite'
import DeleteIcon from '@mui/icons-material/Delete'
import { useState } from 'react'
import { IconButton } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { Paragraph } from 'src/feature/localization/component/atom/Paragraph'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

type RemoveProjectUserProps = {
  userName: string
  onRemove: () => void
  disabled?: boolean
}

export const RemoveProjectUser = observer(({
  userName,
  onRemove,
  disabled = false,
}: RemoveProjectUserProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  const [isConfirming, setIsConfirming] = useState(false)

  const handleRemove = () => {
    if (disabled) {
      return
    }

    onRemove()
    setIsConfirming(false)
  }

  return (
    <AppBox sx={styles.container}>
      <IconButton
        aria-label={`remove ${userName}`}
        onClick={() => setIsConfirming(true)}
        aria-disabled={disabled}
        sx={{
          ...styles.deleteIconWrapper,
          ...(disabled ? styles.disabledIconWrapper : {}),
        }}
      >
        <DeleteIcon sx={styles.deleteIcon} />
      </IconButton>

      <ModalWindow
        modalProps={{
          open: isConfirming,
          onClose: () => setIsConfirming(false),
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', md: '60%', lg: '30%' },
              borderRadius: '10px',
            },
          },
        }}
      >
        <AppBox sx={styles.modal}>
          <Paragraph>
            {projectBuilderTranslation.removeUserConfirmation({ userName })}
          </Paragraph>
          <AppDivider />
          <AppBox sx={styles.modalActions}>
            <AppButton
              fontSize={12}
              textValue={projectBuilderTranslation.cancel()}
              variant='light'
              onClick={() => setIsConfirming(false)}
            />
            <AppButton
              fontSize={12}
              textValue={projectBuilderTranslation.removeUserButton()}
              variant='contained'
              disabled={disabled}
              onClick={handleRemove}
            />
          </AppBox>
        </AppBox>
      </ModalWindow>
    </AppBox>
  )
})

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
  },
  deleteIconWrapper: {
    width: 25,
    height: 25,
    borderRadius: '50%',
    border: '1px solid',
    borderColor: 'error.main',
    backgroundColor: 'background.dark',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'scale(1.05)',
      color: 'error.main',
    },
  },
  disabledIconWrapper: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  deleteIcon: {
    fontSize: 14,
    color: 'error.main',
  },
  modal: {
    flexDirection: 'column',
    gap: 10,
  },
  modalActions: {
    gap: 10,
    justifyContent: 'flex-end',
  },
} as const